import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Gift, Package, RefreshCw, Users } from 'lucide-react';
import { Button, Header, Select, Status, Table, can, dateTime, dayLabel, number, op, queryString, useOpsContext, useQuery } from './ui.jsx';

export function OperationsDashboard() {
  const { auth, stores } = useOpsContext();
  const [storeId, setStoreId] = useState('');
  const { data, loading, error, reload } = useQuery(`${op}/dashboard${queryString({ store_id: storeId })}`);
  const m = data?.metrics || {};
  const cards = [
    ['bookings_today', 'Booking hôm nay', CalendarDays, 'blue', 'bookings.view'],
    ['bookings_checked_in', 'Khách đang ở quán', Users, 'cyan', 'bookings.view'],
    ['customers', 'Khách thành viên', Users, 'violet', 'customers.view'],
    ['redemptions_today', 'Đổi quà hôm nay', Gift, 'pink', 'redemptions.view'],
    ['low_stock', 'Quà sắp hết', Package, 'orange', 'reward_inventory.view'],
    ['out_of_stock', 'Quà đã hết', Package, 'green', 'reward_inventory.view'],
  ].filter(([, , , , permission]) => can(auth, permission));
  const statusOrder = ['pending', 'confirmed', 'checked_in', 'completed', 'cancelled', 'no_show'];

  return <main className="content-page">
    <Header eyebrow="TỔNG QUAN" title="Tổng quan vận hành" description={data ? `Ngày ${dayLabel(data.business_date)} · số liệu trong phạm vi cơ sở bạn được phân quyền` : 'Booking, thành viên, đổi quà và tồn kho'}>
      {stores.length > 1 && <Select label="Cơ sở" value={storeId} onChange={setStoreId} options={stores} placeholder="Tất cả cơ sở" />}
      <Button onClick={reload}><RefreshCw size={16} className={loading ? 'spin' : ''} /> Làm mới</Button>
    </Header>
    {error && <div className="ops-empty error">{error}</div>}
    <section className="metric-grid">{cards.map(([key, label, Icon, tone]) => <article className={`metric-card ${tone}`} key={key}><div><Icon /></div><span>{label}</span><strong>{number.format(Number(m[key] || 0))}</strong></article>)}</section>

    {data && <div className="dash-grid">
      {can(auth, 'bookings.view') && <section className="card panel">
        <div className="panel-title"><h2>Booking hôm nay theo trạng thái</h2><Link to="/app/booking">Mở lịch đặt</Link></div>
        <div className="status-strip">{statusOrder.map((status) => <div key={status}><Status value={status} /><strong>{number.format(data.bookings_by_status[status] || 0)}</strong></div>)}</div>
        <h3 className="sub-title">Booking sắp tới</h3>
        <Table rows={data.upcoming_bookings} empty="Không có booking sắp tới" columns={[
          { key: 'starts_at', label: 'Giờ đặt', render: dateTime }, { key: 'store_name', label: 'Cơ sở' },
          { key: 'resource_name', label: 'Máy', render: (v, row) => `${v} · ${row.resource_type}` },
          { key: 'customer_name', label: 'Khách' }, { key: 'phone_normalized', label: 'SĐT' },
          { key: 'status', label: 'Trạng thái', render: (v) => <Status value={v} /> },
        ]} />
      </section>}

      {can(auth, 'reward_inventory.view') && <section className="card panel">
        <div className="panel-title"><h2>Tồn kho quà theo cơ sở</h2><Link to="/app/inventory">Mở tồn kho</Link></div>
        <Table rows={data.stock_by_store} rowKey={(row) => row.store_id} empty="Chưa có danh mục quà" columns={[
          { key: 'store_name', label: 'Cơ sở' }, { key: 'total_quantity', label: 'Tổng tồn', align: 'right', render: (v) => number.format(v) },
          { key: 'low_stock', label: 'Sắp hết', align: 'right' }, { key: 'out_of_stock', label: 'Hết hàng', align: 'right' },
        ]} />
        <h3 className="sub-title">Quà sắp hết và đã hết</h3>
        <Table rows={data.stock_alerts} rowKey={(row) => `${row.store_id}-${row.reward_item_id}`} empty="Tất cả quà đều trên ngưỡng cảnh báo" columns={[
          { key: 'store_name', label: 'Cơ sở' }, { key: 'reward_name', label: 'Tên món' }, { key: 'quantity', label: 'Tồn', align: 'right' },
          { key: 'low_stock_threshold', label: 'Ngưỡng', align: 'right' }, { key: 'stock_status', label: 'Trạng thái', render: (v) => <Status value={v} /> },
        ]} />
      </section>}

      {can(auth, 'redemptions.view') && <section className="card panel">
        <div className="panel-title"><h2>Membership và đổi quà</h2><Link to="/app/redemptions">Mở lịch sử đổi quà</Link></div>
        <div className="status-strip">
          <div><span>Khách thành viên</span><strong>{number.format(m.customers || 0)}</strong></div>
          <div><span>Lượt đổi hôm nay</span><strong>{number.format(m.redemptions_today || 0)}</strong></div>
          <div><span>Điểm dùng hôm nay</span><strong>{number.format(m.points_used_today || 0)}</strong></div>
          <div><span>Tổng lượt đổi</span><strong>{number.format(m.redemptions || 0)}</strong></div>
          <div><span>Tổng điểm đã sử dụng</span><strong>{number.format(m.points_used || 0)}</strong></div>
        </div>
      </section>}
    </div>}
  </main>;
}

